import React, { useEffect, useState } from 'react';
import { getArticleBySlug, getArticles, slugify, getMediaUrl } from '../services/api';
import ShareButtons from '../components/ShareButtons';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { Link, useParams } from 'react-router-dom';
import { formatDate, formatDateTime } from '../utils/formatDate';
import { formatCategoryName } from '../utils/formatCategory';
import RichTextRenderer from '../components/RichTextRenderer';

interface ArticleDetailProps {
  slug: string;
  openPdf?: (url: string) => void;
}

const ArticleDetail: React.FC<ArticleDetailProps> = ({ slug, openPdf }) => {
  const { t } = useTranslation();
  const { locale, setAlternatePaths, setIsLoadingContent } = useLanguage();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = (lang && ['tr', 'en', 'ar', 'krm'].includes(lang)) ? lang : locale;

  const [article, setArticle] = useState<any>(null);
  const [kadininKalemiArticles, setKadininKalemiArticles] = useState<any[]>([]);
  const [basinArticles, setBasinArticles] = useState<any[]>([]);
  const [kulturArticles, setKulturArticles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const [prevSlug, setPrevSlug] = useState(slug);
  const [prevLocale, setPrevLocale] = useState(currentLang);

  if (slug !== prevSlug || currentLang !== prevLocale) {
    setPrevSlug(slug);
    setPrevLocale(currentLang);
    setArticle(null);
    setLoading(true);
    if (setAlternatePaths) setAlternatePaths(null);
  }

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      if (setIsLoadingContent) setIsLoadingContent(true);
      try {
        const [articleData, kadininData, basinData, kulturData] = await Promise.all([
          getArticleBySlug(slug, currentLang),
          getArticles(undefined, currentLang, 1, 4, 'KadininKalemi'),
          getArticles(undefined, currentLang, 1, 4, 'Basin'),
          getArticles(undefined, currentLang, 1, 4, 'Kultur')
        ]);
        setArticle(articleData);

        if (articleData) {
          const paths: Record<string, string> = {};
          paths[currentLang] = `/${currentLang}/haberler/${slug}`;
          
          if (articleData.localizations) {
            const locs = articleData.localizations.data || articleData.localizations;
            if (Array.isArray(locs)) {
              locs.forEach((loc: any) => {
                  const langName = loc.attributes?.locale || loc.locale;
                  const targetLang = langName === 'kmr' ? 'krm' : langName;
                  const locSlug = loc.attributes?.slug || loc.slug || slugify(loc.attributes?.title || loc.title) || loc.documentId || loc.id;
                  if (targetLang && locSlug) {
                      paths[targetLang] = `/${targetLang}/haberler/${locSlug}`;
                  }
              });
            }
          }
          if (setAlternatePaths) setAlternatePaths(paths);
        } else {
          if (setAlternatePaths) setAlternatePaths({});
        }
        
        const filterAndSlice = (data: any) => {
          if (!data || !data.data) return [];
          return data.data
            .filter((item: any) => (item.documentId || item.id) !== (articleData?.documentId || articleData?.id))
            .slice(0, 3);
        };

        setKadininKalemiArticles(filterAndSlice(kadininData));
        setBasinArticles(filterAndSlice(basinData));
        setKulturArticles(filterAndSlice(kulturData));
      } catch (error) {
        console.error("Failed to load article details", error);
      } finally {
        setLoading(false);
        if (setIsLoadingContent) setIsLoadingContent(false);
      }
    };

    if (slug) {
      fetchArticle();
    }
  }, [slug, currentLang, setAlternatePaths, setIsLoadingContent]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowRight') setLightboxIndex(prev => (prev !== null && article?.galleryImages ? (prev + 1) % article.galleryImages.length : null));
      if (e.key === 'ArrowLeft') setLightboxIndex(prev => (prev !== null && article?.galleryImages ? (prev - 1 + article.galleryImages.length) % article.galleryImages.length : null));
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, article]);

  // Scroll to top when loading finishes to ensure the page starts at the top
  useEffect(() => {
    if (!loading) {
      window.scrollTo(0, 0);
    }
  }, [loading]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-brand-purple-900"></div>
      </div>
    );
  }

  const emptyMessages: Record<string, { title: string; desc: string; button: string }> = {
    tr: {
      title: "İçerik Mevcut Değil",
      desc: "Bu haber/içerik seçilen dilde henüz yayınlanmamış veya çevrilmemiştir.",
      button: "Haberlere Dön"
    },
    en: {
      title: "Content Not Available",
      desc: "This article/content has not been published or translated in the selected language yet.",
      button: "Back to News"
    },
    krm: {
      title: "Naverok Peyda Nebû",
      desc: "Ev nûçe/naverok hîn bi zimanê hilbijartî nehatiye weşandin an wergerandin.",
      button: "Vegere Nûçeyan"
    },
    ar: {
      title: "المحتوى غير متوفر",
      desc: "هذا الخبر/المحتوى لم يتم نشره أو ترجمته باللغة المختارة بعد.",
      button: "العودة إلى الأخبار"
    }
  };

  const msg = emptyMessages[currentLang] || emptyMessages.tr;

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-neutral-bg px-4 py-20 animate-in fade-in duration-500">
        <div className="max-w-md w-full bg-white rounded-[2rem] p-8 md:p-10 shadow-xl border border-brand-purple-50/50 text-center space-y-6">
          <div className="w-16 h-16 bg-brand-purple-50 rounded-full flex items-center justify-center mx-auto text-brand-purple-600">
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-serif font-bold text-brand-purple-900">{msg.title}</h2>
            <p className="text-gray-500 text-sm leading-relaxed font-light">{msg.desc}</p>
          </div>
          <div className="pt-4">
            <Link 
              to={`/${currentLang}/haberler`} 
              className="inline-flex w-full items-center justify-center bg-brand-purple-900 text-white px-8 py-4 rounded-xl font-bold shadow-lg hover:bg-brand-purple-700 hover:-translate-y-0.5 active:scale-95 transition-all"
            >
              {msg.button}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const imageUrl = article.coverImage?.url
    ? getMediaUrl(article.coverImage.url)
    : 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?q=80&w=1200&auto=format&fit=crop';

  const isGallery = (typeof article?.category === 'string' ? (article.category.toLowerCase() === 'galeri' || article.category.toLowerCase() === 'gallery') : (article?.category?.name?.toLowerCase() === 'galeri' || article?.category?.name?.toLowerCase() === 'gallery')) || (article?.galleryImages && article.galleryImages.length > 0);

  if (isGallery) {
    return (
      <article className="min-h-screen bg-brand-neutral-bg pt-32 pb-20 animate-in fade-in duration-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Gallery Header */}
          <div className="mb-12 space-y-6 text-center">
            <span className="inline-block px-4 py-1.5 bg-brand-yellow-400 text-brand-purple-900 text-xs font-bold uppercase tracking-widest rounded-full shadow-sm">
              {formatCategoryName(article?.category, t)}
            </span>
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-brand-purple-900 leading-tight">
              {article.title}
            </h1>
            {article.description && (
              <p className="text-lg text-gray-600 font-light leading-relaxed">
                {article.description}
              </p>
            )}
            {article.content && (
              <RichTextRenderer
                content={article.content}
                className="prose prose-lg prose-purple max-w-none text-center mx-auto mt-12 mb-12 text-gray-600 leading-relaxed font-light [&_p]:mb-6"
              />
            )}
            <div className="text-sm text-gray-400 pt-4">
              {article.publishedDate && <span>{formatDate(article.publishedDate, currentLang)}</span>}
            </div>
            <div className="flex justify-center mt-6">
              <ShareButtons title={article.title} />
            </div>
          </div>

          {/* Masonry Layout for Gallery */}
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6">
            {article.galleryImages && article.galleryImages.map((img: any, idx: number) => (
              <div 
                key={img.id} 
                className="break-inside-avoid relative group overflow-hidden rounded-2xl cursor-pointer shadow-sm hover:shadow-xl transition-all"
                onClick={() => setLightboxIndex(idx)}
              >
                <img 
                  src={getMediaUrl(img.url)} 
                  alt={img.alternativeText || article.title} 
                  className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-brand-purple-900/0 group-hover:bg-brand-purple-900/20 transition-colors duration-500"></div>
              </div>
            ))}
          </div>

          {/* Lightbox Modal */}
          {lightboxIndex !== null && article.galleryImages && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 animate-in fade-in duration-200" onClick={() => setLightboxIndex(null)}>
              <button 
                onClick={(e) => { e.stopPropagation(); setLightboxIndex(null); }}
                className="absolute top-6 right-6 text-white/70 hover:text-white p-2 z-50"
              >
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
              
              <button 
                onClick={(e) => { e.stopPropagation(); setLightboxIndex(prev => (prev !== null ? (prev - 1 + article.galleryImages.length) % article.galleryImages.length : null)); }}
                className="absolute left-4 md:left-10 text-white/50 hover:text-white p-4 z-50"
              >
                <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" /></svg>
              </button>

              <div className="relative max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
                <img 
                  src={getMediaUrl(article.galleryImages[lightboxIndex].url)} 
                  alt={article.galleryImages[lightboxIndex].alternativeText || article.title}
                  className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
                />
                <div className="absolute -bottom-10 left-0 right-0 text-center text-white/70 text-sm font-medium tracking-widest">
                  {lightboxIndex + 1} / {article.galleryImages.length}
                </div>
              </div>

              <button 
                onClick={(e) => { e.stopPropagation(); setLightboxIndex(prev => (prev !== null ? (prev + 1) % article.galleryImages.length : null)); }}
                className="absolute right-4 md:right-10 text-white/50 hover:text-white p-4 z-50"
              >
                <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>
          )}

        </div>
      </article>
    );
  }

  return (
    <article className="min-h-screen bg-brand-neutral-bg pt-32 pb-20 animate-in fade-in duration-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left Column (Main Article) */}
          <div className="lg:col-span-8 space-y-12">
            
            {/* Header (Title, Date, Author) */}
            <div className="space-y-6">
              <span className="inline-block px-4 py-1.5 bg-brand-yellow-400 text-brand-purple-900 text-xs font-bold uppercase tracking-widest rounded-full shadow-sm">
                {formatCategoryName(article.category, t)}
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-brand-purple-900 leading-tight">
                {article.title}
              </h1>
              
              <div className="flex items-center space-x-6 text-sm font-medium text-gray-500 pt-4">
                {article.writer && (
                  <div className="flex items-center gap-3">
                    {article.writer.avatar?.url ? (
                      <img src={getMediaUrl(article.writer.avatar.url)} alt={article.writer.name} className="w-14 h-14 rounded-full object-cover shadow-sm border-2 border-white" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-brand-purple-100 flex items-center justify-center text-brand-purple-900 font-bold text-sm shadow-sm border-2 border-white">
                        {article.writer.name.substring(0, 2).toUpperCase()}
                      </div>
                    )}
                    <span className="text-brand-purple-900 font-bold">{article.writer.name}</span>
                  </div>
                )}
                {article.publishedDate && article.writer && <span className="w-1.5 h-1.5 bg-brand-purple-200 rounded-full"></span>}
                {article.publishedDate && <span className="uppercase tracking-widest">{formatDate(article.publishedDate, currentLang)}</span>}
              </div>
            </div>

            {/* Hero Image (smaller, rounded) */}
            <div className="w-full aspect-video max-h-[500px] rounded-[2rem] overflow-hidden shadow-xl shadow-brand-purple-900/10 relative">
              <img
                src={imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Content */}
            <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-sm border border-brand-purple-50">
              {article.documentFile && openPdf && (
                <div className="mb-12">
                  <button
                    onClick={() => openPdf(getMediaUrl(article.documentFile.url))}
                    className="inline-flex items-center space-x-3 bg-brand-purple-900 text-white px-8 py-4 rounded-xl font-bold shadow-lg hover:-translate-y-1 hover:bg-brand-purple-700 transition-all"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    <span>{t('news_page.view_pdf')}</span>
                  </button>
                </div>
              )}

              {(article?.category === 'Etkinlikler' || article?.category === 'events' || article?.category?.name === 'Etkinlikler' || article?.category?.name === 'Events') && (
                <div className="flex flex-col sm:flex-row items-center gap-4 mb-10 pb-10 border-b border-gray-100">
                  <div className="flex-1 w-full bg-gray-50 rounded-2xl p-6 flex items-start gap-6">
                    <div className="w-12 h-12 rounded-full bg-brand-green-500/10 flex items-center justify-center shrink-0">
                      <svg className="w-6 h-6 text-brand-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    </div>
                    <div>
                      <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">{t('news_page.date_time')}</span>
                      <span className="text-brand-purple-900 font-bold">
                        {article.eventDate ? formatDateTime(article.eventDate, currentLang) : t('news_page.no_date')}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex-1 w-full bg-gray-50 rounded-2xl p-6 flex items-start gap-6">
                    <div className="w-12 h-12 rounded-full bg-brand-yellow-500/10 flex items-center justify-center shrink-0">
                      <svg className="w-6 h-6 text-brand-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    </div>
                    <div>
                      <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">{t('news_page.venue')}</span>
                      <span className="text-brand-purple-900 font-bold line-clamp-2">
                        {article.eventVenue || t('news_page.no_venue')}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <RichTextRenderer
                content={article.content}
                className="prose prose-lg prose-purple max-w-none text-gray-600 leading-relaxed font-light [&_p]:mb-6"
              />
              <div className="mt-12">
                <ShareButtons title={article.title} />
              </div>
            </div>
          </div>

          {/* Right Column (Sidebar) */}
          <div className="lg:col-span-4 space-y-8">
            <div className="bg-white rounded-[2rem] p-8 shadow-sm border border-gray-100 sticky top-32 space-y-10">
              
              {/* Kadının Kaleminden */}
              {kadininKalemiArticles.length > 0 && (
                <div>
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-2 h-6 bg-brand-green-500 rounded-full"></div>
                    <h3 className="text-xl font-serif font-bold text-brand-purple-900">{t('kalem_page.title')}</h3>
                  </div>
                  <div className="space-y-4">
                    {kadininKalemiArticles.map((latest: any) => {
                      const latestImg = latest.coverImage?.url ? getMediaUrl(latest.coverImage.url) : 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?q=80&w=200';
                      return (
                        <Link key={latest.documentId || latest.id} to={`/${currentLang}/haberler/${latest.slug || latest.documentId || latest.id}`} className="group flex items-start gap-6 p-2 -mx-2 rounded-xl hover:bg-brand-neutral-bg transition-colors">
                          <div className="w-20 h-20 shrink-0 rounded-xl overflow-hidden shadow-sm">
                            <img src={latestImg} alt={latest.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                          </div>
                          <div className="flex flex-col justify-center h-20">
                            <h4 className="text-lg font-bold text-brand-purple-900 leading-snug group-hover:text-brand-purple-600 transition-colors line-clamp-2 mb-1.5">{latest.title}</h4>
                            {latest.publishedDate && <span className="text-[10px] text-gray-400 mt-1">{formatDate(latest.publishedDate, currentLang)}</span>}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Basın Bültenleri */}
              {basinArticles.length > 0 && (
                <div>
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-2 h-6 bg-brand-yellow-500 rounded-full"></div>
                    <h3 className="text-xl font-serif font-bold text-brand-purple-900">{t('news_page.cat_press')}</h3>
                  </div>
                  <div className="space-y-4">
                    {basinArticles.map((latest: any) => {
                      const latestImg = latest.coverImage?.url ? getMediaUrl(latest.coverImage.url) : 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?q=80&w=200';
                      return (
                        <Link key={latest.documentId || latest.id} to={`/${currentLang}/haberler/${latest.slug || latest.documentId || latest.id}`} className="group flex items-start gap-6 p-2 -mx-2 rounded-xl hover:bg-brand-neutral-bg transition-colors">
                          <div className="w-20 h-20 shrink-0 rounded-xl overflow-hidden shadow-sm">
                            <img src={latestImg} alt={latest.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                          </div>
                          <div className="flex flex-col justify-center h-20">
                            <h4 className="text-lg font-bold text-brand-purple-900 leading-snug group-hover:text-brand-purple-600 transition-colors line-clamp-2 mb-1.5">{latest.title}</h4>
                            {latest.publishedDate && <span className="text-[10px] text-gray-400 mt-1">{formatDate(latest.publishedDate, currentLang)}</span>}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Kültür Sanat */}
              {kulturArticles.length > 0 && (
                <div>
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-2 h-6 bg-brand-purple-500 rounded-full"></div>
                    <h3 className="text-xl font-serif font-bold text-brand-purple-900">{t('news_page.cat_culture')}</h3>
                  </div>
                  <div className="space-y-4">
                    {kulturArticles.map((latest: any) => {
                      const latestImg = latest.coverImage?.url ? getMediaUrl(latest.coverImage.url) : 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?q=80&w=200';
                      return (
                        <Link key={latest.documentId || latest.id} to={`/${currentLang}/haberler/${latest.slug || latest.documentId || latest.id}`} className="group flex items-start gap-6 p-2 -mx-2 rounded-xl hover:bg-brand-neutral-bg transition-colors">
                          <div className="w-20 h-20 shrink-0 rounded-xl overflow-hidden shadow-sm">
                            <img src={latestImg} alt={latest.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                          </div>
                          <div className="flex flex-col justify-center h-20">
                            <h4 className="text-lg font-bold text-brand-purple-900 leading-snug group-hover:text-brand-purple-600 transition-colors line-clamp-2 mb-1.5">{latest.title}</h4>
                            {latest.publishedDate && <span className="text-[10px] text-gray-400 mt-1">{formatDate(latest.publishedDate, currentLang)}</span>}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}

            </div>
          </div>

        </div>
      </div>
    </article>
  );
};

export default ArticleDetail;
